import type { WebRtcObservation } from "../shared/contracts";
import { isPublicIp, normalizeIp } from "../shared/validation";

/** Parse the candidate address position only, not related-address fields. */
export function normalizeIceCandidate(candidate: string): { type: "host" | "srflx" | "relay"; publicIp?: string } | null {
  const parts = candidate.trim().split(/\s+/);
  if (parts.length < 8 || !parts[0].startsWith("candidate:") || parts[6] !== "typ") return null;
  const type = parts[7];
  if (type !== "host" && type !== "srflx" && type !== "relay") return null;
  const normalized = normalizeIp(parts[4]);
  return { type, ...(type === "srflx" && normalized && isPublicIp(normalized.ip) ? { publicIp: normalized.ip } : {}) };
}

export async function observeWebRtcPublicIps(timeoutMs = 1800): Promise<WebRtcObservation> {
  if (!("RTCPeerConnection" in window)) return { supported: false, additionalPublicIps: [], completed: false };
  const observed = new Set<string>();
  const types = new Set<"host" | "srflx" | "relay">();
  let connection: RTCPeerConnection;
  try { connection = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.cloudflare.com:3478" }] }); }
  catch { return { supported: true, additionalPublicIps: [], completed: false }; }
  return new Promise((resolve) => {
    let finished = false;
    const finish = (completed = false) => {
      if (finished) return;
      finished = true;
      window.clearTimeout(timer);
      connection.close();
      resolve({ supported: true, additionalPublicIps: [...observed], candidateTypes: [...types], completed });
    };
    const timer = window.setTimeout(() => finish(), timeoutMs);
    connection.onicecandidate = (event) => {
      if (!event.candidate) { finish(true); return; }
      const parsed = normalizeIceCandidate(event.candidate.candidate);
      if (parsed) { types.add(parsed.type); if (parsed.publicIp) observed.add(parsed.publicIp); }
    };
    try {
      connection.createDataChannel("observation");
      void connection.createOffer().then((offer) => connection.setLocalDescription(offer)).catch(() => finish());
    } catch { finish(); }
  });
}
